/**
 * Auth Context 접근 공통 훅
 * 여러 파일에서 반복되는 getContext('auth') 패턴을 통합
 * 
 * 사용 예:
 * const auth = useAuth();
 * const adminAuth = useAdminAuth();
 */

import { getContext } from 'svelte';

// 타입 정의 - AppProvider.svelte에서 export된 타입들을 여기서 재정의
type AuthService = {
    state: {
        user: any;
        isAuthenticated: boolean;
        loading: boolean;
    };
    googleLogin(): Promise<void>;
    appleLogin(): Promise<void>;
    logout(): Promise<void>;
};

type NutritionService = any;
type WorkoutService = any;
type HealthService = any;
type ChallengeService = any;
type SocialService = any;
type AdminAuthService = any;

/**
 * 일반 사용자 Auth Context를 가져오는 훅
 * @returns AuthService 인스턴스
 * @throws Error Auth Context가 없는 경우
 */
export function useAuth(): AuthService {
    const auth = getContext<AuthService>('auth');
    
    if (!auth) {
        throw new Error('useAuth must be used within AppProvider');
    }
    
    return auth;
}

/**
 * 관리자 Auth Context를 가져오는 훅  
 * @returns AdminAuthService 인스턴스
 * @throws Error AdminAuth Context가 없는 경우
 */
export function useAdminAuth(): AdminAuthService {
    const adminAuth = getContext<AdminAuthService>('adminAuth');
    
    if (!adminAuth) {
        throw new Error('useAdminAuth must be used within AdminAppProvider');
    }
    
    return adminAuth;
}

/**
 * Auth Context가 존재하는지 안전하게 확인하는 훅
 * @returns AuthService 인스턴스 또는 null
 */
export function useOptionalAuth(): AuthService | null {
    return getContext<AuthService>('auth') || null;
}

/**
 * AdminAuth Context가 존재하는지 안전하게 확인하는 훅
 * @returns AdminAuthService 인스턴스 또는 null  
 */
export function useOptionalAdminAuth() {
    return getContext('adminAuth') || null;
}

/**
 * Google Fit Context를 가져오는 훅
 * @returns GoogleFitService 인스턴스
 * @throws Error GoogleFit Context가 없는 경우
 */
export function useGoogleFit() {
    const googleFit = getContext('googleFit');
    
    if (!googleFit) {
        throw new Error('useGoogleFit must be used within AppProvider');
    }
    
    return googleFit;
}

/**
 * 현재 사용자 정보를 직접 가져오는 편의 훅
 * @returns User 인스턴스 또는 null
 */
export function useCurrentUser() {
    const auth = useOptionalAuth();
    return auth?.state?.user || null;
}

/**
 * 현재 관리자 사용자 정보를 직접 가져오는 편의 훅
 * @returns AdminUser 인스턴스 또는 null
 */
export function useCurrentAdminUser() {
    const adminAuth = useOptionalAdminAuth() as any;
    return adminAuth?.state?.user || null;
}

/**
 * 로그인 상태를 확인하는 편의 훅
 * @returns boolean
 */
export function useIsAuthenticated(): boolean {
    const auth = useOptionalAuth();
    return auth?.state?.isAuthenticated || false;
}

/**
 * 관리자 로그인 상태를 확인하는 편의 훅
 * @returns boolean
 */
export function useIsAdminAuthenticated(): boolean {
    const adminAuth = useOptionalAdminAuth() as any;
    return adminAuth?.state?.isAuthenticated || false;
}

/**
 * Nutrition Context를 가져오는 훅
 * @returns NutritionService 인스턴스
 * @throws Error Nutrition Context가 없는 경우
 */
export function useNutrition(): NutritionService {
    const nutrition = getContext<NutritionService>('nutrition');
    
    if (!nutrition) {
        throw new Error('useNutrition must be used within AppProvider');
    }
    
    return nutrition;
}

/**
 * Nutrition Context가 존재하는지 안전하게 확인하는 훅
 * @returns NutritionService 인스턴스 또는 null
 */
export function useOptionalNutrition(): NutritionService | null {
    return getContext<NutritionService>('nutrition') || null;
}

/**
 * Workout Context를 가져오는 훅
 * @returns WorkoutService 인스턴스
 * @throws Error Workout Context가 없는 경우
 */
export function useWorkout(): WorkoutService {
    const workout = getContext<WorkoutService>('workout');
    
    if (!workout) {
        throw new Error('useWorkout must be used within AppProvider');
    }
    
    return workout;
}

/**
 * Workout Context가 존재하는지 안전하게 확인하는 훅
 * @returns WorkoutService 인스턴스 또는 null
 */
export function useOptionalWorkout(): WorkoutService | null {
    return getContext<WorkoutService>('workout') || null;
}

/**
 * Health Context를 가져오는 훅
 * @returns HealthService 인스턴스
 * @throws Error Health Context가 없는 경우
 */
export function useHealth(): HealthService {
    const health = getContext<HealthService>('health');
    
    if (!health) {
        throw new Error('useHealth must be used within AppProvider');
    }
    
    return health;
}

/**
 * Health Context가 존재하는지 안전하게 확인하는 훅
 * @returns HealthService 인스턴스 또는 null
 */
export function useOptionalHealth(): HealthService | null {
    return getContext<HealthService>('health') || null;
}

/**
 * Challenge Context를 가져오는 훅
 * @returns ChallengeService 인스턴스
 * @throws Error Challenge Context가 없는 경우
 */
export function useChallenge(): ChallengeService {
    const challenge = getContext<ChallengeService>('challenge');

    if (!challenge) {
        throw new Error('useChallenge must be used within AppProvider');
    }

    return challenge;
}

/**
 * Challenge Context가 존재하는지 안전하게 확인하는 훅
 * @returns ChallengeService 인스턴스 또는 null
 */
export function useOptionalChallenge(): ChallengeService | null {
    return getContext<ChallengeService>('challenge') || null;
}

/**
 * Social Context를 가져오는 훅
 * @returns SocialService 인스턴스
 * @throws Error Social Context가 없는 경우
 */
export function useSocial(): SocialService {
    const social = getContext<SocialService>('social');

    if (!social) {
        throw new Error('useSocial must be used within AppProvider');
    }

    return social;
}

/**
 * Social Context가 존재하는지 안전하게 확인하는 훅
 * @returns SocialService 인스턴스 또는 null
 */
export function useOptionalSocial(): SocialService | null {
    return getContext<SocialService>('social') || null;
}